import { Component, computed, effect, input, signal } from '@angular/core';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { Dataset, GraficaData } from '../../grafica-manez.interface';

const EMPTY_GRAFICA: GraficaData = { labels: [], datasets: [{ label: '', data: [] }] };

@Component({
	selector: 'app-grafica-velocidad-manez-lozano',
	standalone: false,
	templateUrl: './grafica-velocidad.component.html',
	styleUrls: ['./grafica-velocidad.component.scss'],
	providers: [MessageService],
})
export class GraficaVelocidadManezLozanoComponent {
    
	grafica_vel = input<GraficaData>(EMPTY_GRAFICA);
	grafica_pressure = input<GraficaData>(EMPTY_GRAFICA);
	grafica_rpm = input<GraficaData>(EMPTY_GRAFICA);
	grafica_flow = input<GraficaData>(EMPTY_GRAFICA);
	grafica_dose = input<GraficaData>(EMPTY_GRAFICA);

	countChart = signal(0);

	readonly elments_per_page = 1440;
	readonly max_multipler = 3;

	max_chart_pages = computed(() => {
		const pages = Math.floor(this.grafica_vel().labels.length / this.elments_per_page);
		return pages === 0 ? 1 : pages;
	});

	data = computed(() => {
		const page = this.countChart();
		const vel = this.grafica_vel();
		const pressure = this.grafica_pressure();
		const rpm = this.grafica_rpm();
		const flow = this.grafica_flow();
		const dose = this.grafica_dose();

		if ((vel.datasets as Dataset[])[0]?.data.length === 0) {
			return this.emptyData();
		}

		const start = this.elments_per_page * page;
		const end = Math.min(start + this.elments_per_page, vel.labels.length);

		return this.buildPageData(vel, pressure, rpm, flow, dose, start, end);
	});

	public options = {
		responsive: false,
		maintainAspectRatio: false,
		plugins: {
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				zoom: {
					wheel: {
						enabled: true,
					},
					pinch: {
						enabled: true,
					},
					mode: 'x',
				},
			},
		},
	};

	constructor() {
		// Resetear pagina al recibir nuevos datos
		effect(() => {
			const vel = this.grafica_vel();
			if (((vel.datasets as Dataset[])[0]?.data.length ?? 0) > 0) {
				this.countChart.set(0);
			}
		});
	}

	private emptyData(): GraficaData {
		return {
			labels: [],
			datasets: [
				{ label: 'Velocidad (km/h)', data: [], fill: false, borderColor: 'green' },
				{ label: 'Presion (bar)', data: [], fill: false, borderColor: '#4bc0c0' },
				{ label: 'Rpm', data: [], fill: false, borderColor: '#aa42f5' },
				{ label: 'Dosis', data: [], fill: false, borderColor: 'brown' },
				{ label: 'Caudal (l/min)', data: [], fill: false, borderColor: 'orange' },
			],
		};
	}

	private buildPageData(
		vel: GraficaData,
		pressure: GraficaData,
		rpm: GraficaData,
		flow: GraficaData,
		dose: GraficaData,
		start: number,
		end: number,
	): GraficaData {
		const labels: string[] = [];
		const speedData: number[] = [];
		const pressureData: number[] = [];
		const rpmData: number[] = [];
		const flowData: number[] = [];
		const doseData: number[] = [];

		let val2Speed = 0;
		let validSpeed = 0;
		let val2Pressure = 0;
		let val2Rpm = 0;
		let val2Flow = 0;
		let val2Dose = 0;
		let validDose = 0;

		for (let i = start; i < end; i++) {
			let dataSpeed = (vel.datasets as Dataset).data[i];
			let dataPressure = (pressure.datasets as Dataset).data[i];
			let dataRpm = (rpm.datasets as Dataset).data[i];
			let dataFlow = (flow.datasets as Dataset).data[i];
			let dataDose = (dose.datasets as Dataset).data[i] ?? 0;

			if (i >= 2) {
				if (dataSpeed) {
					if (val2Speed) {
						validSpeed = val2Speed;
						if (dataSpeed > validSpeed * this.max_multipler) {
							dataSpeed = val2Speed;
						}
					} else if (validSpeed !== 0) {
						val2Speed = validSpeed;
						if (dataSpeed > validSpeed * this.max_multipler) {
							dataSpeed = val2Speed;
						}
					}
				}

				if (val2Pressure && dataPressure && dataPressure > val2Pressure * this.max_multipler) {
					dataPressure = val2Pressure;
				}

				if (val2Rpm && dataRpm && dataRpm > val2Rpm * this.max_multipler) {
					dataRpm = val2Rpm;
				}

				if (val2Flow && dataFlow && dataFlow > val2Flow * this.max_multipler) {
					dataFlow = val2Flow;
				}

				if (dataDose) {
					if (val2Dose) {
						validDose = val2Dose;
						if (dataDose > validDose * this.max_multipler) {
							dataDose = val2Dose;
						}
					} else if (validDose) {
						val2Dose = validDose;
						if (dataDose > validDose * this.max_multipler) {
							dataDose = val2Dose;
						}
					}
				}
			}

			val2Speed = dataSpeed ?? 0;
			val2Pressure = dataPressure ?? 0;
			val2Rpm = dataRpm ?? 0;
			val2Flow = dataFlow ?? 0;
			val2Dose = dataDose ?? 0;

			labels.push(vel.labels[i] ?? '');
			speedData.push(dataSpeed ?? 0);
			pressureData.push(dataPressure ?? 0);
			rpmData.push(dataRpm ?? 0);
			flowData.push(dataFlow ?? 0);
			doseData.push(dataDose ?? 0);
		}

		return {
			labels,
			datasets: [
				{ label: 'Velocidad (km/h)', data: speedData, fill: false, borderColor: 'green' },
				{ label: 'Presion (bar)', data: pressureData, fill: false, borderColor: '#4bc0c0' },
				{ label: 'Rpm', data: rpmData, fill: false, borderColor: '#aa42f5' },
				{ label: 'Dosis', data: doseData, fill: false, borderColor: 'brown' },
				{ label: 'Caudal (l/min)', data: flowData, fill: false, borderColor: 'orange' },
			],
		};
	}

	replaceContent(operation: string) {
		const maxPages = this.max_chart_pages();
		this.countChart.update((count) => {
			switch (operation) {
				case 'begin':
					return 0;
				case 'previous':
					return Math.max(0, count - 1);
				case 'next':
					return Math.min(maxPages - 1, count + 1);
				case 'final':
					return maxPages - 1;
				default:
					return count;
			}
		});
	}

	selectData() {}

	compareHours(labels: string[]) {
		return new Promise((resolve) => {
			const beginningTime = labels[0]?.split(':') ?? '';

			for (let index = 0; index < labels.length; index++) {
				const endOfTime = labels[index];

				const diff = moment(endOfTime, 'hh:mm:ss')
					.subtract(beginningTime[0], 'hours')
					.subtract(beginningTime[1], 'minutes')
					.subtract(beginningTime[2], 'seconds');

				if (diff.hours() >= 2) {
					resolve(true);
					break;
				}

				if (index + 1 >= labels.length) {
					resolve(true);
				}
			}
		});
	}
}
